/**
 * @fileoverview iconMap — the ONE closed lucide registry: explicit named imports, keyed by name.
 *
 * String icon identities everywhere in the fleet — nav `icon` / `iconName` keys, dashboard group and
 * widget icons, command-palette actions, the `<Icon name>` adapters in pos-web and payday-web, the
 * SKL-004 skill icons — resolve through THIS map. It is the load-bearing bundle contract: a
 * `import * as LucideIcons from 'lucide-react'` namespace map defeats lucide's per-icon tree-shaking
 * BY CONSTRUCTION and ships the entire 1,642-module icon set (~851 KB minified) into every app's
 * vendor-icons chunk, of which ~11% was ever used (PERF-FECSS-001 P14, 2026-08-02);
 * `web/no-lucide-namespace-import` bans the namespace and barrel forms.
 *
 * PLANNER-BOARD-UX-003 U22 (owner ruling R-46, 2026-10-09): until this package the fleet carried
 * FIVE closed registries — page-shell's `resolveLucideIcon.jsx` (132 names), pos-web's and
 * payday-web's `shared/constants/iconMap.js` (144 and 33, each behind its own `<Icon>` adapter),
 * command-palette's `paletteIconMap.jsx` (40, 10 with a consumer) and skills-ui's `skillIconMap.jsx`
 * (36, the SKL-004 allow-list) — and which one judged a name depended on the render path, not the
 * file. The command palette's rows now come from the nav tree, so one surface read two
 * vocabularies at once. This map is their union (202 names); the SKL-004 allow-list stays a list of
 * NAMES (`@puffin-core/unified-models` `skillIconNames.json`) that resolves here.
 *
 * Adding a new icon name to a nav config, the DCS-001 catalog, a palette action or an `<Icon>` site
 * requires adding it here AND to `iconNames.js` — `tests/unit/iconMap.test.js` fails when the two
 * disagree, and `tests/cross-app/contracts/appShellNavConfig.contract.test.js` fails when a nav
 * config or a palette registry names an icon this map lacks. The cost of one map line buys the
 * fleet its tree-shaking. Keep both lists alphabetical (case-insensitive); the `*Icon`-suffixed
 * keys are lucide's own alias exports, kept because config data references them.
 *
 * @module @puffin-core/icon-registry/iconMap
 */

import {
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Anchor,
  Archive,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightLeft,
  ArrowUpDown,
  AudioWaveform,
  Award,
  BadgeCheck,
  Banknote,
  BarChart,
  BarChart2,
  BarChart3,
  Bell,
  BookOpen,
  BookTemplate,
  Bot,
  Box,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Cable,
  Calculator,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  Camera,
  Check,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coffee,
  Cog,
  Columns,
  Copy,
  CreditCard,
  Crown,
  Database,
  DollarSign,
  DoorClosed,
  DoorOpen,
  Download,
  Edit,
  ExternalLink,
  Eye,
  EyeOff,
  FileAudio,
  FileCheck,
  FileClock,
  FilePlus,
  FileQuestion,
  FileSearch,
  FileSignature,
  FileSpreadsheet,
  FileText,
  Filter,
  Fingerprint,
  FingerprintIcon,
  Flag,
  Folder,
  FolderCog,
  FolderKanban,
  FolderOpen,
  Frame,
  GitBranch,
  GitCompare,
  Globe,
  GraduationCap,
  Grid,
  GripVertical,
  HardHat,
  Hash,
  Headphones,
  Headset,
  HeartPulse,
  History,
  Home,
  Inbox,
  Info,
  Key,
  KeyIcon,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Library,
  LifeBuoy,
  Lightbulb,
  LineChart,
  Link,
  List,
  ListChecks,
  ListTodo,
  Loader,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Mailbox,
  MailIcon,
  Map,
  MapPin,
  MapPinned,
  Megaphone,
  MessageSquare,
  MessageSquareHeart,
  Mic,
  Monitor,
  MoreHorizontal,
  MoreVertical,
  MousePointerClick,
  Move,
  Network,
  Package,
  PackageCheck,
  PackageOpen,
  PackageX,
  Pencil,
  PencilRuler,
  Percent,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  PieChart,
  Play,
  PlayCircle,
  Plug,
  Plus,
  PlusSquare,
  Printer,
  Receipt,
  RefreshCw,
  RotateCcw,
  Route,
  Rss,
  ScanLine,
  Scissors,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Settings2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldIcon,
  ShoppingBag,
  ShoppingCart,
  Siren,
  Sliders,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Sparkles,
  Sprout,
  Stamp,
  Store,
  Tablet,
  Tag,
  Target,
  Timer,
  ToggleRight,
  Trash2,
  TrendingUp,
  Trophy,
  Truck,
  Undo2,
  Upload,
  User,
  UserCheck,
  UserCircle,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  UserX,
  UtensilsCrossed,
  Voicemail,
  Volume2,
  Wallet,
  Warehouse,
  Wrench,
  X,
  XCircle,
  Zap,
} from 'lucide-react';

export const ICON_MAP = Object.freeze({
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Anchor,
  Archive,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightLeft,
  ArrowUpDown,
  AudioWaveform,
  Award,
  BadgeCheck,
  Banknote,
  BarChart,
  BarChart2,
  BarChart3,
  Bell,
  BookOpen,
  BookTemplate,
  Bot,
  Box,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Cable,
  Calculator,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  Camera,
  Check,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coffee,
  Cog,
  Columns,
  Copy,
  CreditCard,
  Crown,
  Database,
  DollarSign,
  DoorClosed,
  DoorOpen,
  Download,
  Edit,
  ExternalLink,
  Eye,
  EyeOff,
  FileAudio,
  FileCheck,
  FileClock,
  FilePlus,
  FileQuestion,
  FileSearch,
  FileSignature,
  FileSpreadsheet,
  FileText,
  Filter,
  Fingerprint,
  FingerprintIcon,
  Flag,
  Folder,
  FolderCog,
  FolderKanban,
  FolderOpen,
  Frame,
  GitBranch,
  GitCompare,
  Globe,
  GraduationCap,
  Grid,
  GripVertical,
  HardHat,
  Hash,
  Headphones,
  Headset,
  HeartPulse,
  History,
  Home,
  Inbox,
  Info,
  Key,
  KeyIcon,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Library,
  LifeBuoy,
  Lightbulb,
  LineChart,
  Link,
  List,
  ListChecks,
  ListTodo,
  Loader,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Mailbox,
  MailIcon,
  Map,
  MapPin,
  MapPinned,
  Megaphone,
  MessageSquare,
  MessageSquareHeart,
  Mic,
  Monitor,
  MoreHorizontal,
  MoreVertical,
  MousePointerClick,
  Move,
  Network,
  Package,
  PackageCheck,
  PackageOpen,
  PackageX,
  Pencil,
  PencilRuler,
  Percent,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  PieChart,
  Play,
  PlayCircle,
  Plug,
  Plus,
  PlusSquare,
  Printer,
  Receipt,
  RefreshCw,
  RotateCcw,
  Route,
  Rss,
  ScanLine,
  Scissors,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Settings2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldIcon,
  ShoppingBag,
  ShoppingCart,
  Siren,
  Sliders,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Sparkles,
  Sprout,
  Stamp,
  Store,
  Tablet,
  Tag,
  Target,
  Timer,
  ToggleRight,
  Trash2,
  TrendingUp,
  Trophy,
  Truck,
  Undo2,
  Upload,
  User,
  UserCheck,
  UserCircle,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  UserX,
  UtensilsCrossed,
  Voicemail,
  Volume2,
  Wallet,
  Warehouse,
  Wrench,
  X,
  XCircle,
  Zap,
});

export const SUPPORTED_ICON_NAMES = Object.freeze(Object.keys(ICON_MAP));
